import { useHistory, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button, Row, Checkbox, Form, Input, Col, Select, Tabs } from "antd";
import { t } from "i18next";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import InstitutionFirmForm from "./InstitutionFirmForm";
import AppointmentTypeSelect from "../appointment/AppointmentTypeSelect";
import Editor from 'react-simple-wysiwyg';
import TinymceEditor from "../../core/components/TinymceEditor";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";

export default function InstitutionCreateEdit({ institutionId, onOk, editable, showFirmSettings }) {
    const [institution, setInstitution] = useState({})
    const [institutionTypes, setInstitutionTypes] = useState([])
    const [html, setHtml] = useState(null);
    const [htmlPrivateNote, setHtmlPrivateNote] = useState(null);
    const editorRef = useRef(null);
    const editorPrivateNoteRef = useRef(null);
    const { hasPermission } = useUser()
    const formRef = useRef();
    const [districts, setDistrict] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);

    const onFinish = async (formData) => {
        if (institutionId) {
            const res = await ApiService.Institution.EditInstitution(institutionId, {
                ...formData,
                note: html,
                privateNote: htmlPrivateNote,
            })
        }
        else {
            const res = await ApiService.Institution.CreateInstitution({
                ...formData,
                note: html,
                privateNote: htmlPrivateNote,
            })

            if (res.success) {
                onOk(res.data)
            }
        }
    }

    const getInstitutionTypes = async () => {
        const res = await ApiService.Institution.GetInstitutionTypes();
        if (res.success) {
            setInstitutionTypes(res.data);
        }
    }

    const getInstitution = async () => {
        const res = await ApiService.Institution.GetInstitution(institutionId);
        if (res.success) {
            setInstitution(res.data)

            if (res.data.cityId) {
                setSelectedCity(res.data.cityId);
            }

            if (res.data.districtId) {
                setSelectedDistrict(res.data.districtId)
            }

            formRef.current.setFieldsValue({
                ...res.data,
                appointmentTypeId: res.data.appointmentType?.id,
            })

            setHtml(res.data.note)
            setHtmlPrivateNote(res.data.privateNote)

            editorRef?.current?.setContent(res.data.note)
            editorPrivateNoteRef?.current?.setContent(res.data.privateNote)
        }
    }

    const getDistricts = async (cityId) => {
        const res = await ApiService.District.GetDistrictList(cityId);
        if (res.success) {
            setDistrict(res.data)
        } else {
            setDistrict([])
        }
    }


    const getCities = async () => {
        const res = await ApiService.City.GetCityList();
        if (res.success) {
            setCities(res.data)
        } else {
            setCities([])
        }
    }

    useEffect(() => {
        if (selectedCity) {
            getDistricts(selectedCity).then(() => {
                if (institution?.districtId) {
                    formRef.current.setFieldsValue({
                        districtId: institution.districtId
                    });
                }
            });
        }
    }, [selectedCity, institution?.districtId]);

    useEffect(() => {
        if (institutionId) {
            getInstitution()
        }
    }, [institutionId])

    useEffect(() => {
        getInstitutionTypes()
        getCities()
    }, [])

    const toTitleCase = (str = '') =>
        str
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    return (
        <Tabs>
            <Tabs.TabPane tab={t('Institution Info')} key={'institution-form'}>
                <Form
                    ref={formRef}
                    layout={'vertical'}
                    onFinish={onFinish}
                    initialValues={{
                        institutionType: 1,
                        enabled: true
                    }}
                >
                    <Form.Item
                        label={t('Name')}
                        name={'name'}
                        rules={[
                            { required: true, message: t('İsim zorunludur') },
                            { min: 3, message: t('İsim en az 3 karakterden oluşmalıdır.') }
                        ]}
                    >
                        <Input
                            readOnly={!editable}
                            onBlur={() => {
                                const value = formRef.current.getFieldValue('name');
                                if (value) {
                                    formRef.current.setFieldsValue({
                                        name: toTitleCase(value),
                                    });
                                }
                            }}
                        />
                    </Form.Item>


                    <Form.Item
                        label={t('Type')}
                        name={'institutionTypeId'}
                        rules={[{ required: true, message: t('Tür seçilmelidir') }]}
                    >
                        <Select
                            style={{ minWidth: 200 }}
                            showSearch
                            placeholder={t('Select type')}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                String(option?.children)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }

                        >
                            {institutionTypes.map((institutionType) => (
                                <Select.Option key={institutionType.id} value={institutionType.id}>
                                    {institutionType.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>


                    <Form.Item
                        label={`${t('Active')} ?`}
                        name="enabled"
                        valuePropName="checked"
                        rules={[{ required: true }]}
                    >
                        <Checkbox disabled={!editable}>{t('Yes')}</Checkbox>
                    </Form.Item>


                    <Form.Item
                        label={t('Appointment Type')}
                        name={'appointmentTypeId'}
                        required={true}
                    >
                        <AppointmentTypeSelect disabled={!editable} />
                    </Form.Item>

                    <Form.Item
                        label={t('Mail')}
                        name={'mail'}
                    >
                        <Input readOnly={!editable} />
                    </Form.Item>

                    <Form.Item
                        label={t('Phone')}
                        name={'phoneNumber'}
                    >
                        <Input readOnly={!editable} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="cityId" label="City">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select a city"
                                    options={cities.map(c => ({ value: c.id, label: c.name }))}
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    value={selectedCity}
                                    onChange={(val) => {
                                        setSelectedCity(val);
                                        setSelectedDistrict(null);
                                        formRef.current.setFieldsValue({ cityId: val, districtId: undefined });
                                        getDistricts(val);
                                    }}

                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="districtId" label="District">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select a district"
                                    options={districts.map(c => ({ value: c.id, label: c.name }))}
                                    value={selectedDistrict}
                                    onChange={(val) => {
                                        setSelectedDistrict(val);
                                        formRef.current.setFieldsValue({ districtId: val });
                                    }}
                                    disabled={!selectedCity}
                                />
                            </Form.Item>
                        </Col>
                    </Row>


                    <Form.Item
                        label={t('Address')}
                        name={'address'}
                    >
                        <Input.TextArea readOnly={!editable} />
                    </Form.Item>

                    {/* <Form.Item
                        label={t('Note')}
                        name={'note'}
                    >
                        {
                            editable && (
                                <TinymceEditor
                                    value={html}
                                    handleChange={(val) => setHtml(val)}
                                    height={200}
                                    onInit={(editor) => editorRef.current = editor}
                                />
                            )
                        }
                        {
                            !editable && (
                                <div dangerouslySetInnerHTML={{ __html: html ?? '<b>--------------</b>' }} />
                            )
                        }
                    </Form.Item> */}
                    {
                        (editable || (hasPermission(PERMISSIONS.VIEW_ALL_INSTITUTIONS))) && (
                            <Form.Item
                                label={t('Note')}
                                name={'note'}
                            >
                                <TinymceEditor
                                    value={html}
                                    handleChange={(val) => setHtml(val)}
                                    height={200}
                                    onInit={(editor) => editorRef.current = editor}
                                />
                            </Form.Item>
                        )
                    }

                    {
                        (hasPermission(PERMISSIONS.FIRM_INSTITUTION_MANAGER) || hasPermission(PERMISSIONS.VIEW_INSTITUTION_NOTE_RESPONSIBLE)
                            || editable) && (
                            <Form.Item
                                label={t('Private Note')}
                                name={'privateNote'}
                            >
                                <TinymceEditor
                                    value={htmlPrivateNote}
                                    handleChange={(val) => setHtmlPrivateNote(val)}
                                    height={200}
                                    onInit={(editor) => editorPrivateNoteRef.current = editor}
                                />
                            </Form.Item>
                        )
                    }

                    <Form.Item>
                        {
                            editable && (<Button htmlType={'submit'} type={'primary'} size={'small'}>{t('Save')}</Button>)
                        }
                    </Form.Item>
                </Form>
            </Tabs.TabPane>
            {
                (hasPermission(PERMISSIONS.FIRM_INSTITUTION_MANAGER) || hasPermission(PERMISSIONS.VIEW_INSTITUTION_NOTE_RESPONSIBLE)) && showFirmSettings && (
                    <Tabs.TabPane tab={t('Firm Settings')} key={'institution-firm'}>
                        <InstitutionFirmForm
                            institutionId={institutionId}
                        />
                    </Tabs.TabPane>
                )
            }
        </Tabs>
    )
}